/* eslint-disable react-doctor/no-cascading-set-state */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
    ipLocationQueryOptions,
    type UserLocation,
} from "../location-estimate";
import { useSelectedLocation } from "../selected-location";

export type { UserLocation } from "../location-estimate";

export type LocationMode = "exact" | "approximate";

type ExactLocationHandlers = {
    onSuccess: (location: UserLocation) => void;
    onError: (message: string) => void;
    onFinally: () => void;
};

const requestExactLocationPromise = () =>
    new Promise<UserLocation>((resolve, reject) => {
        requestExactLocation({
            onSuccess: resolve,
            onError: (message) => reject(new Error(message)),
            onFinally: () => {},
        });
    });

const requestExactLocation = ({
    onSuccess,
    onError,
    onFinally,
}: ExactLocationHandlers) => {
    if (typeof window === "undefined" || !navigator.geolocation) {
        onError("Geolocation is not supported by your browser");
        onFinally();
        return;
    }

    navigator.geolocation.getCurrentPosition(
        (position) => {
            onSuccess({
                lat: position.coords.latitude,
                lon: position.coords.longitude,
            });
            onFinally();
        },
        (error) => {
            onError(error.message || "Unable to get exact location");
            onFinally();
        },
    );
};

export function useUserLocation() {
    const selectedLocation = useSelectedLocation();
    const [exactLocation, setExactLocation] = useState<UserLocation | null>(
        null,
    );
    const [exactLoading, setExactLoading] = useState(false);
    const [exactError, setExactError] = useState<string | null>(null);
    const isBrowser = typeof window !== "undefined";

    const {
        data: approximateLocationData,
        error: approximateLocationError,
        isFetching: approximateLocationIsFetching,
        isPending: approximateLocationIsPending,
    } = useQuery({
        ...ipLocationQueryOptions(),
        enabled: isBrowser,
    });

    const { data: exactPermissionGranted = false } = useQuery({
        queryKey: ["geolocation-permission"],
        queryFn: async () => {
            if (!("permissions" in navigator)) return false;
            const status = await navigator.permissions.query({
                name: "geolocation",
            });
            return status.state === "granted";
        },
        enabled: isBrowser,
        staleTime: Number.POSITIVE_INFINITY,
    });
    const {
        data: automaticallyGrantedLocation,
        error: automaticallyGrantedError,
        isFetching: automaticallyGrantedLoading,
    } = useQuery({
        queryKey: ["exact-location", "permission-granted"],
        queryFn: requestExactLocationPromise,
        enabled: exactPermissionGranted,
        staleTime: Number.POSITIVE_INFINITY,
        retry: false,
    });

    const requestForExact = () => {
        setExactLoading(true);
        setExactError(null);

        requestExactLocation({
            onSuccess: (location) => {
                setExactLocation(location);
            },
            onError: (message) => {
                setExactError(message);
            },
            onFinally: () => {
                setExactLoading(false);
            },
        });
    };

    const approximateLocation = approximateLocationData ?? null;
    const approximateError =
        approximateLocationError instanceof Error
            ? approximateLocationError.message
            : approximateLocationError
              ? "Failed to fetch approximate location"
              : null;

    const resolvedExactLocation =
        exactLocation ??
        (selectedLocation ? null : automaticallyGrantedLocation);
    const location =
        resolvedExactLocation ??
        (selectedLocation
            ? { lat: selectedLocation.lat, lon: selectedLocation.lng }
            : approximateLocation);
    const mode: LocationMode | null = resolvedExactLocation
        ? "exact"
        : location
          ? "approximate"
          : null;
    const loading =
        exactLoading ||
        (!selectedLocation &&
            (automaticallyGrantedLoading || approximateLocationIsFetching));
    const error =
        exactError ??
        (automaticallyGrantedError instanceof Error
            ? automaticallyGrantedError.message
            : null) ??
        approximateError;

    return {
        location,
        mode,
        requestForExact,
        loading,
        error,
        isApproximatePending: isBrowser && approximateLocationIsPending,
    };
}

export function calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
): number {
    const R = 3959;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) *
            Math.cos((lat2 * Math.PI) / 180) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c);
}
